import {Component, OnInit, OnDestroy, Input} from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import {DialogService} from '@rbaip/ui-component';
import { Asset, ASSET_STATUS_COLLAPSED_TO_INVENTORY, ASSET_STATUS_NOT_SOLD, assetMessage,
  AssetSaleInfo, AuditAssetMove, RBA_LIVE, RESELL_COLLAPSE_TO_INVENTORY, RESELL_NOT_SOLD
} from '../../shared/domain/item';
import {Opportunity, OpportunityContract} from '../../shared/domain/opportunity';
import {ItemSummaryAssetControlGroupService} from '../../shared/asset-package/item-summary-asset-control-group.service';
import {ItemSummaryAssetsService} from '../../shared/asset/item-summary-assets.service';
import {ContractType} from '../../shared/domain/contract';
import {ItemSummaryItemService} from "../../shared/asset";
import {MoveAssetsComponent} from "../move-assets/move-assets.component";

@Component({
  selector: 'lib-item-summary-resell-collapsed-asset',
  templateUrl: './resell-collapsed-asset.component.html',
  styleUrls: ['./resell-collapsed-asset.component.scss']
})
export class ResellCollapsedAssetComponent implements OnInit, OnDestroy {

  @Input('selectedItems') selectedItems: Asset[];
  @Input('itemService')  itemService: ItemSummaryItemService;
  @Input('opportunity') opportunity: Opportunity;
  @Input('auditAssetMove') auditAssetMove: AuditAssetMove;

  public isContractSelectionScreenVisible = true;
  public assetSaleInfo: AssetSaleInfo;
  public displayPrices = false;
  public enableResellButton : boolean = true;
  public selectedItem: Asset;

  public ASSET_STATUS_COLLAPSED_TO_INVENTORY = ASSET_STATUS_COLLAPSED_TO_INVENTORY;
  public ASSET_STATUS_NOT_SOLD = ASSET_STATUS_NOT_SOLD;

  private assetControlGroupId: number;
  private selectedContract: OpportunityContract;
  private assetsToResell: Asset[];
  private targetAuditAssetMove: AuditAssetMove = new AuditAssetMove();

  private assetPackageSubscription: Subscription;

  constructor ( private dialogRef: MatDialogRef<MoveAssetsComponent>,
              private assetControlGroupService: ItemSummaryAssetControlGroupService,
              private assetsService: ItemSummaryAssetsService,
              private dialogService: DialogService ) {

  }

  ngOnInit() {
    this.dialogService.showProgress();
    this.assetsToResell = this.selectedItems;
    this.validateAsset();
    this.assetControlGroupId = this.assetsToResell[0].assetControlGroupId;
    this.selectedItem = this.assetsToResell[0];
    if ( this.selectedItem.assetStatusId === ASSET_STATUS_COLLAPSED_TO_INVENTORY ) {
      this.targetAuditAssetMove.destinationType = RESELL_COLLAPSE_TO_INVENTORY;
    }
    this.subscribeAssetPackage();
  }

  ngOnDestroy() {
    if ( this.assetPackageSubscription ) {
      this.assetPackageSubscription.unsubscribe();
    }
  }

  public selectContract(selectedContract): void {
    this.selectedContract = selectedContract;
  }

  public isNextButtonEnabled(): boolean {
    return !!this.selectedContract;
  }

  public navigateToEventSelection(): void {
    if (this.isAuctionEventContract()) {
      this.resellAsset();
    } else {
      this.isContractSelectionScreenVisible = false;
    }
  }

  public isResellButtonEnabled(): boolean {
    return ( !!this.selectedContract || this.assetsToResell[0].assetStatusId === ASSET_STATUS_NOT_SOLD ) &&  this.enableResellButton;
  }

  public displaySaleInfo(): boolean {
    return !this.isContractSelectionScreenVisible && !!this.assetSaleInfo && !this.isAuctionEventContract();
  }

  public isAuctionEventContract(): boolean {
    if (!this.selectedContract || !this.selectedContract.salesHubContractType) {
      return false;
    } else {
      return ContractType.isAuctionEventContract(this.selectedContract.salesHubContractType);
    }
  }

  public resellAsset(): void {
    const asset = this.assetsToResell[0];
    if (asset.assetStatusId === ASSET_STATUS_COLLAPSED_TO_INVENTORY){
      this.resellCollapsedItem();
    } else {
      this.resellNotSoldItem();
    }
  }

  private validateResell(): boolean {
    if (!this.selectedContract.ipContractId) {
      this.dialogService.showMessageDialog(assetMessage.contractMoveOppWithNoContractError);
      return false;
    }
    return true;
  }

  private loadSaleInfo(opportunityId: number): void {
    this.assetSaleInfo = new AssetSaleInfo();
    this.assetSaleInfo.displayOnlyFutureEvents = true;
    this.assetSaleInfo.saleType = RBA_LIVE;
    this.assetSaleInfo.opportunityId = opportunityId;
    this.assetSaleInfo.highlightRequiredField = true;
    this.assetSaleInfo.isEditable = true;
    this.assetSaleInfo.displayPrices = true;
    this.assetSaleInfo.displayListingFee = false;
    this.assetSaleInfo.displaySetDefaultCheckBox = false;
    this.assetSaleInfo.displayBudget = false;
    this.assetSaleInfo.isAuctionSite = true;
    this.assetSaleInfo.assetControlGroupId = this.assetControlGroupId;
  }

  private setAssetsWithSaleInfoAndAudit(): void {
    if (!ContractType.isAuctionEventContract(this.selectedContract.salesHubContractType)) {
      this.assetsToResell.forEach(asset => {
        AssetSaleInfo.resetSaleInfoForAsset(asset);
        this.assetSaleInfo.setAssetWithSaleInfo(asset);
        asset.auditAssetMove = this.targetAuditAssetMove;
        asset.auditAssetMove.assetId = asset.assetId;
      });
    } else {
      this.setAssetWithAuditInfo();
    }
  }

  private setAssetWithAuditInfo(): void {
    this.assetsToResell.forEach(item => {
      item.auditAssetMove = this.targetAuditAssetMove;
      item.auditAssetMove.assetId = item.assetId;
    });
  }

  private subscribeAssetPackage(): void {
    this.assetPackageSubscription = this.assetControlGroupService.getAssetPackageInfo(this.assetControlGroupId).subscribe( assetControlGroup => {
      this.targetAuditAssetMove.currentAssetControlGroup = assetControlGroup;
      this.loadSaleInfo(assetControlGroup.opportunityId);
    });
  }

  private validateAsset() : void {
    this.checkRelistedItems();
  }

  private checkRelistedItems() : void {
    const equipIds = this.assetsService.getEquipIdsFromAssets(this.assetsToResell);
    this.assetsService.getAssetsFromEquipIds(equipIds).subscribe(eligibleAssets => {
      const selectedAsset = eligibleAssets[0];
      if (selectedAsset.assetStatusId === ASSET_STATUS_NOT_SOLD || selectedAsset.assetStatusId === ASSET_STATUS_COLLAPSED_TO_INVENTORY) {
        this.dialogService.hideProgress();
        const relistedItemsYn = eligibleAssets.some(asset => asset.destinationEquipmentId);
        if (relistedItemsYn) {
          this.enableResellButton = false;
          this.auditAssetMove.destinationType = null;
          this.dialogService.showMessageDialog( assetMessage.assetMoveToRelistedConfirmation);
        }
      }
    }, error => {
        this.dialogService.showMessage('Error processing lots. Please contact Tech Support.');
        this.dialogService.hideProgress();
    });
  }

  private resellNotSoldItem() : void {
    this.dialogService.showProgress();
    this.assetsToResell.forEach(asset => {
      const auditAssetMove = new AuditAssetMove();
      auditAssetMove.destinationType = RESELL_NOT_SOLD;
      asset.auditAssetMove = auditAssetMove;
      asset.auditAssetMove.assetId = asset.assetId;
    });
    this.itemService.resellNotSoldAssets(this.opportunity.sellerContractId, this.assetsToResell)
      .subscribe(itemsResponse => {
        if ( itemsResponse && itemsResponse.error ) {
          this.dialogService.showMessage(itemsResponse.message);
          this.dialogService.hideProgress();
        } else {
          let failedAssetsCount = 0;
          if ( itemsResponse ) {
            itemsResponse.forEach(itemResponse => {
              if (itemResponse.status != 'Success') { failedAssetsCount++; }
            });
          }
          let message = '';
          if ( failedAssetsCount === 1 ) {
            message = itemsResponse[0].message;
          } else {
            message = 'Lot successfully processed.';
          }
          this.dialogService.showMessage(message);
          this.dialogRef.close(true);
          this.dialogService.hideProgress();
        }
      }, error => {
        this.dialogService.showMessage('Unexpected error occurred while reselling not sold item. Please contact tech support. ');
        this.dialogRef.close(true);
        this.dialogService.hideProgress();
      });
  }

  private resellCollapsedItem() : void {
    if ( !this.validateResell() ) {
      return;
    }
    this.dialogService.showProgress();
    this.setAssetsWithSaleInfoAndAudit();
    this.itemService.resellNotSoldAssets(this.selectedContract.ipContractId, this.assetsToResell)
      .subscribe(itemsResponse => {
        if ( itemsResponse && itemsResponse.error ) {
          this.dialogService.showMessage(itemsResponse.message);
          this.dialogService.hideProgress();
        } else {
          let failedAssetsCount = 0;
          if ( itemsResponse ) {
            itemsResponse.forEach(itemResponse => {
              if ( itemResponse.status != 'Success' ) {
                failedAssetsCount++;
              }
            });
          }
          let message = '';
          if ( failedAssetsCount === 1 ) {
            message = itemsResponse[0].message;
          } else if ( failedAssetsCount > 1 ) {
            message = failedAssetsCount + ' out of ' + itemsResponse.length +
              ' assets failed to process. Please contact tech support. ';
            if ( failedAssetsCount == itemsResponse.length ) {
              message = 'Unexpected error occurred while processing assets. Please contact tech support. ';
            }
          } else {
            message = 'Lots successfully processed.';
          }
          this.dialogService.showMessage(message);
          this.dialogRef.close(true);
          this.dialogService.hideProgress();
        }
      } , error => {
        this.dialogService.showMessage('Unexpected error occurred while processing lots. Please contact tech support. ');
        this.dialogRef.close(true);
        this.dialogService.hideProgress();
      });
  }

}
